import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Typography, 
    Box, 
    CircularProgress, 
    Alert,
    Pagination,
    Button,
    Container
} from '@mui/material';
import {
    ArrowBack,
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import { ShortStoryCard } from '../ShortStory';
import PageContainer from '../layout/PageContainer';

const ShortStories = () => {
    const navigate = useNavigate();
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 8; // Fixed page size for public stories

    // Fetch stories when page changes
    useEffect(() => {
        const fetchStories = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString()
            });
            
            const response = await userRequest.get(`/contentShortStory?${params}`);
            
            setStories(response.data.stories);
            setTotalPages(response.data.pagination.totalPages);
            
        } catch (error) {
            console.error('Error fetching stories:', error);
            setError('Failed to load stories. Please try again later.');
            setStories([]);
        } finally {
            setLoading(false);
        }
        };
        
        fetchStories();
    }, [currentPage]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Mock topics for now
    const mockTopics = ['Technology', 'Science', 'History', 'Literature', 'Business', 'Health'];

    const [selectedTopics, setSelectedTopics] = useState([]);

    const handleTopicToggle = (topic) => {
        setSelectedTopics(prev => 
            prev.includes(topic) 
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    return (
        <PageContainer>
            <Container maxWidth="lg" sx={{ py: 4, backgroundColor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/profile')}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight="600" sx={{ flex: 1, textAlign: 'center' }}>
                        Short Reading
                    </Typography>
                    <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
                </Box>

                {/* Topic Buttons */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                        }}
                    >
                        {mockTopics.map((topic) => (
                            <Button
                                key={topic}
                                variant={selectedTopics.includes(topic) ? 'contained' : 'outlined'}
                                onClick={() => handleTopicToggle(topic)}
                                sx={{
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    boxShadow: selectedTopics.includes(topic) ? 3 : 0,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {topic}
                            </Button>
                        ))}
                    </Box>
                </Box>

            {/* TODO: Enable search bar. Disable for now as there are only few stories. */}
            {/* Search Bar */}
            {/* <Box sx={{ mb: 4 }}>
                <TextField
                    fullWidth
                    placeholder="Search stories by title, author, or tags..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        maxWidth: 600,
                        margin: '0 auto',
                        display: 'block'
                    }}
                />
            </Box> */}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress size={60} />
                </Box>
            )}

            {/* Stories Grid */}
            {!loading && !error && stories.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {stories.map((story) => (
                            <Box key={story._id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)', lg: 'calc(25% - 22.5px)' } }}>
                                <ShortStoryCard story={story} showTags={false} />
                            </Box>
                        ))}
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Empty State */}
            {!loading && !error && stories.length === 0 && (
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    minHeight="300px"
                    textAlign="center"
                >
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                        No stories available yet
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Check back later for new stories
                    </Typography>
                </Box>
            )}
            </Container>
        </PageContainer>
    );
};

export default ShortStories; 